import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, CONTACT } from "../../data/content";
import { MEDIA } from "../../data/media";
import { scrollToId } from "../../lib/motion";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-sage-light/50" : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          <button
            onClick={() => go("home")}
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
            aria-label="Ffyona Duncan Therapies home"
          >
            <img src={MEDIA.logo} alt="Ffyona Duncan Therapies" className="h-11 w-auto" />
          </button>

          <ul className="hidden lg:flex items-center gap-9">
            {NAV.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => go(item.id)}
                  data-testid={`nav-${item.id}`}
                  className="relative text-sm font-medium text-ink-soft hover:text-sage-dark transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-sage-dark after:transition-all after:duration-300 hover:after:w-full"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <div className="hidden lg:block">
            <button
              onClick={() => go("contact")}
              data-testid="nav-book-btn"
              className="rounded-full bg-sage-dark hover:bg-sage-deep text-cream px-6 py-3 text-sm font-medium tracking-wide shadow-soft hover:shadow-float transition-all duration-300 active:scale-[0.98]"
            >
              Free consultation
            </button>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-ink"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-cream/98 backdrop-blur-xl pt-24 px-8"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col gap-2">
              {NAV.map((item, i) => (
                <motion.li
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <button
                    onClick={() => go(item.id)}
                    data-testid={`mobile-nav-${item.id}`}
                    className="font-serif text-4xl text-ink py-2 block"
                  >
                    {item.label}
                  </button>
                </motion.li>
              ))}
            </ul>
            <button
              onClick={() => go("contact")}
              className="mt-8 w-full rounded-full bg-sage-dark text-cream px-6 py-4 text-base font-medium"
              data-testid="mobile-book-btn"
            >
              Book your free consultation
            </button>
            <p className="mt-8 text-ink-soft text-sm">
              <a href={CONTACT.phoneHref} className="block py-1">{CONTACT.phone}</a>
              <a href={`mailto:${CONTACT.email}`} className="block py-1">{CONTACT.email}</a>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
